import { cn } from '../../utils/cn';

type BuildStatusBadgeType = 'project' | 'build';

export interface BuildStatusColorConfig {
  label: string;
  bg: string;
  text: string;
  border: string;
  tooltip: string;
}

const PROJECT_STATUS_CONFIG: Record<string, BuildStatusColorConfig> = {
  planning: {
    bg: 'bg-blue-500/15',
    text: 'text-blue-300',
    border: 'border-blue-500/25',
    label: 'Planning',
    tooltip: 'Project is in the planning phase before work begins',
  },
  in_progress: {
    bg: 'bg-amber-500/15',
    text: 'text-amber-300',
    border: 'border-amber-500/25',
    label: 'In Progress',
    tooltip: 'Active work is underway on this project',
  },
  review: {
    bg: 'bg-purple-500/15',
    text: 'text-purple-300',
    border: 'border-purple-500/25',
    label: 'Review',
    tooltip: 'Project is under team or lead review',
  },
  completed: {
    bg: 'bg-green-500/15',
    text: 'text-green-300',
    border: 'border-green-500/25',
    label: 'Completed',
    tooltip: 'Project has been finished and signed off',
  },
  on_hold: {
    bg: 'bg-yellow-500/15',
    text: 'text-yellow-300',
    border: 'border-yellow-500/25',
    label: 'On Hold',
    tooltip: 'Work is paused temporarily',
  },
  cancelled: {
    bg: 'bg-red-500/15',
    text: 'text-red-300',
    border: 'border-red-500/25',
    label: 'Cancelled',
    tooltip: 'Project was cancelled and will not continue',
  },
};

const BUILD_STATUS_CONFIG: Record<string, BuildStatusColorConfig> = {
  planned: {
    bg: 'bg-slate-500/15',
    text: 'text-slate-300',
    border: 'border-slate-500/25',
    label: 'Planned',
    tooltip: 'Build is scheduled but work has not started',
  },
  in_progress: {
    bg: 'bg-amber-500/15',
    text: 'text-amber-300',
    border: 'border-amber-500/25',
    label: 'In Progress',
    tooltip: 'Build work is actively in progress',
  },
  review: {
    bg: 'bg-purple-500/15',
    text: 'text-purple-300',
    border: 'border-purple-500/25',
    label: 'Review',
    tooltip: 'Build is awaiting review or feedback',
  },
  revision_needed: {
    bg: 'bg-orange-500/15',
    text: 'text-orange-300',
    border: 'border-orange-500/25',
    label: 'Revision Needed',
    tooltip: 'Changes are required before the build can proceed',
  },
  completed: {
    bg: 'bg-green-500/15',
    text: 'text-green-300',
    border: 'border-green-500/25',
    label: 'Completed',
    tooltip: 'Build is finished',
  },
  on_hold: {
    bg: 'bg-yellow-500/15',
    text: 'text-yellow-300',
    border: 'border-yellow-500/25',
    label: 'On Hold',
    tooltip: 'Build work is paused temporarily',
  },
  cancelled: {
    bg: 'bg-red-500/15',
    text: 'text-red-300',
    border: 'border-red-500/25',
    label: 'Cancelled',
    tooltip: 'Build was cancelled',
  },
};

const DEFAULT_PROJECT_CONFIG = PROJECT_STATUS_CONFIG.planning;
const DEFAULT_BUILD_CONFIG = BUILD_STATUS_CONFIG.planned;

interface BuildStatusBadgeProps {
  status: string;
  type: BuildStatusBadgeType;
  size?: 'sm' | 'md';
  className?: string;
}

export const getStatusColor = (
  status: string,
  type: BuildStatusBadgeType
): BuildStatusColorConfig => {
  if (type === 'project') {
    return PROJECT_STATUS_CONFIG[status] ?? DEFAULT_PROJECT_CONFIG;
  }
  return BUILD_STATUS_CONFIG[status] ?? DEFAULT_BUILD_CONFIG;
};

export const BuildStatusBadge = ({
  status,
  type,
  size = 'sm',
  className,
}: BuildStatusBadgeProps) => {
  const config = getStatusColor(status, type);

  return (
    <span
      className={cn(
        'badge badge-dot',
        config.bg,
        config.text,
        config.border,
        size === 'md' && 'text-xs px-2.5 py-1',
        className
      )}
      title={config.tooltip}
    >
      {config.label}
    </span>
  );
};
